import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import logo from "@/assets/logo.png";

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Les deux mots de passe ne correspondent pas");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="mx-auto flex max-w-md flex-col items-center px-4 py-16">
      <div className="w-full rounded-2xl border border-amber-800/15 bg-white p-8 shadow-lg shadow-amber-900/5">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src={logo} alt="Pétales de Miel" data-testid="reset-password-logo" className="h-14 w-14 object-contain" />
          <h1 className="font-serif text-3xl font-semibold text-bark">Nouveau mot de passe</h1>
          <p className="text-sm text-bark-soft">Choisissez un mot de passe de 8 caractères minimum.</p>
        </div>

        {done ? (
          <div data-testid="reset-done" className="space-y-4 text-center">
            <CheckCircle2 size={40} className="mx-auto text-sage" />
            <p className="text-sm text-bark-soft">Votre mot de passe est mis à jour — vous pouvez vous connecter.</p>
            <Link
              to="/compte/connexion"
              data-testid="reset-goto-login"
              className="block w-full rounded-full bg-amber-deep py-3.5 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98]"
            >
              Me connecter
            </Link>
          </div>
        ) : !token ? (
          <div data-testid="reset-no-token" className="space-y-4 text-center">
            <p className="text-sm text-bark-soft">Ce lien est incomplet — utilisez le lien reçu par e-mail.</p>
            <Link to="/compte/connexion" data-testid="reset-back-login" className="inline-block text-sm font-medium text-amber-deep hover:text-bark">
              Retour à la connexion
            </Link>
          </div>
        ) : (
          <form data-testid="reset-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="rp-password" className="mb-1.5 block text-sm font-medium text-bark">Nouveau mot de passe</label>
              <PasswordInput id="rp-password" data-testid="rp-password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} placeholder="8 caractères minimum" />
            </div>
            <div>
              <label htmlFor="rp-confirm" className="mb-1.5 block text-sm font-medium text-bark">Confirmez le mot de passe</label>
              <PasswordInput id="rp-confirm" data-testid="rp-confirm" required minLength={8} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputClass} placeholder="Retapez-le pour être sûr·e" />
            </div>
            {error && (
              <p data-testid="reset-error" className="rounded-xl bg-terra/10 px-4 py-3 text-sm font-medium text-terra">
                {error}
              </p>
            )}
            <button
              data-testid="reset-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-amber-deep py-3.5 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? "Mise à jour…" : "Enregistrer mon nouveau mot de passe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
